import { useCallback, useMemo, useState } from 'react';
import {
  ELEMENT_GROUP_PREFIX,
  SignalStudioHighlight,
  SignalStudioHighlightConstraints,
  SignalStudioPreviewElementProps,
} from '@fullstory/signal-studio-context';
import { GroupedObjectInfo, ObjectCollection } from '@fullstory/renderer-shared';
import { NamedElementRecord } from '@fullstory/proto/fs/services/search-settings';
import { PlatformSelector } from '@fullstory/proto/fs/api/segment';
import { namedElementsSelectors } from '@fullstory/redux';
import { useSelector } from 'react-redux';

const { getNamedElementsById } = namedElementsSelectors;

interface useElementPropertyRuleHighlightsProps {
  selectors: PlatformSelector[] | undefined;
  childSelectors: PlatformSelector[] | undefined;
  hasChild: boolean;
  hasChildAndNonEmptyParent: boolean;
  isMulti: boolean;
  constrainInspected?: boolean;
}

export const useElementPropertyRuleHighlights = ({
  selectors,
  childSelectors,
  hasChild,
  hasChildAndNonEmptyParent,
  isMulti,
  constrainInspected,
}: useElementPropertyRuleHighlightsProps) => {
  const namedElementsById = useSelector(getNamedElementsById);

  const highlights = useMemo(() => {
    const highlights: SignalStudioHighlight[] = [];

    const inspectedSelectors = selectors ?? [];
    inspectedSelectors.forEach(sel => {
      highlights.push({
        type: 'inspected',
        target: sel,
      });
    });
    // sometimes we don't have a child (i.e. the rule only applies to the main element directly)
    // in this case we fallback to the main element selectors giving it both inspected border and context shading
    const contextSelectors = childSelectors ?? selectors ?? [];
    contextSelectors.forEach(sel => {
      highlights.push({
        type: 'inspected-context',
        target: sel,
        includeInnerText: true,
      });
    });

    return highlights;
  }, [selectors, childSelectors]);

  // highlights for the inspected context (i.e. the children from which the property is extracted)
  // must be contained within the inspected elements when there is a parent child relationship
  // additionally, we only ever highlight the least-depth matches if there are multiple in the same chain
  const highlightConstraints = useMemo(
    (): SignalStudioHighlightConstraints => ({
      constraints: hasChildAndNonEmptyParent
        ? [{ type: 'inspected-context', constrainedTo: 'inspected', nested: true }]
        : undefined,
      requireLeastDepth: true,
      constrainInspected,
    }),
    [hasChildAndNonEmptyParent, constrainInspected],
  );

  const previewElements = useMemo(
    (): SignalStudioPreviewElementProps => ({
      constrainedTo: 'inspected',
    }),
    [],
  );

  const [highlightedObjects, setHighlightedObjects] = useState<ObjectCollection>({});
  const [valueSourceObjects, setValueSourceObjects] = useState<{
    [parentPath: string]: GroupedObjectInfo[];
  }>({});
  const [visibleElements, setVisibleElements] = useState<NamedElementRecord[]>([]);
  const [attachedElements, setAttachedElements] = useState<NamedElementRecord[]>([]);
  const onHighlightedObjects = useCallback(
    (highlightedObjects: ObjectCollection, unconstrainedHighlightedObjects: ObjectCollection) => {
      const valueSourceObjects: { [parentPath: string]: GroupedObjectInfo[] } = {};
      const visibleElementIds = new Set<string>();
      const visibleElements: NamedElementRecord[] = [];
      const attachedElementIds = new Set<string>();
      const attachedElements: NamedElementRecord[] = [];
      const processElements = (
        object: GroupedObjectInfo,
        handled: Set<string>,
        elements: NamedElementRecord[],
      ) => {
        object.groups.forEach(group => {
          if (group.startsWith(ELEMENT_GROUP_PREFIX)) {
            const id = group.slice(ELEMENT_GROUP_PREFIX.length);
            if (handled.has(id)) {
              return;
            }
            handled.add(id);
            const namedElementRecord = namedElementsById.get(id);
            if (namedElementRecord) {
              elements.push(namedElementRecord);
            }
          }
        });
      };
      Object.values(highlightedObjects).forEach(object => {
        // values are extracted based on the core element / parent element -- this is the 'inspected' group
        // when a child exists, we take either the best child or all children based on the multi value setting
        // otherwise, we will end up taking all results for a single element (non parent / child relationship)
        if (object.groups.includes('inspected')) {
          if (hasChild) {
            const descendants = object.descendants ?? [];
            let leastDepth = Number.MAX_VALUE;
            const valueObjects: GroupedObjectInfo[] = [];
            for (const descendant of descendants) {
              const descendantObject = highlightedObjects[descendant.path];
              // we only want to look at inspected-context values
              if (!descendantObject?.groups?.includes('inspected-context')) {
                continue;
              }
              if (isMulti) {
                // we take everything, we want all values
                valueObjects.push(descendantObject);
              } else if (descendant.depth < leastDepth) {
                // we take the best, we want only one in the single value case
                // we only take a later descendant if it is explicitly a lesser depth
                // the descendants are ordered in tree order, we want the first at a given depth
                leastDepth = descendant.depth;
                valueObjects[0] = descendantObject;
              }
            }
            if (valueObjects.length) {
              valueSourceObjects[object.path] = valueObjects;
            }
          } else if (object.groups.includes('inspected-context')) {
            // should always be true for single element rules (inspected and inspected-context both in groups)
            // but still double check the groups to be sure
            valueSourceObjects[object.path] = [object];
          }
        }
        // Process attached elements
        processElements(object, attachedElementIds, attachedElements);
      });
      Object.values(unconstrainedHighlightedObjects).forEach(object => {
        // Process visible elements
        processElements(object, visibleElementIds, visibleElements);
      });
      setHighlightedObjects(highlightedObjects);
      setVisibleElements(visibleElements);
      setAttachedElements(attachedElements);
      setValueSourceObjects(valueSourceObjects);
    },
    [namedElementsById, hasChild, isMulti],
  );

  return {
    highlights,
    highlightConstraints,
    previewElements,
    highlightedObjects,
    valueSourceObjects,
    visibleElements,
    attachedElements,
    onHighlightedObjects,
  };
};
